import { useEffect, useState, type FormEvent } from 'react'
import {
  PASSWORD_MIN_LENGTH,
  tokenFromFragment,
  type AuthProblem,
  type CharacterSummary,
  type Locale,
  type ValidationProblem,
} from '@rollcall/contracts'
import { ApiError, AuthRefused, ValidationRefused, type Api } from '../api.js'
import type { Session } from '../auth/session.js'
import { useT } from './i18n-context.js'

/**
 * Экраны аутентификации мастера (D8).
 *
 * Форм здесь пять, и все они устроены одинаково: поле, кнопка, одна
 * строка отказа. Отказ приходит кодом, а не текстом — и код решения
 * сервера (`AuthRefused`), и замечание к полю (`ValidationRefused`), —
 * а текст ему подбирает словарь: иначе интерфейс зависел бы
 * от формулировки на сервере, к тому же английской.
 *
 * Общая строка «не вышло» осталась ровно там, где причина неизвестна
 * (`authFailureText`). Раньше она показывалась на всё подряд, включая
 * «пароль короче десяти знаков», который сервер прекрасно называл.
 *
 * Чего здесь нет: OAuth. Не потому, что руки не дошли, — это
 * юридическое ограничение, аудитория РФ (D8).
 */

type T = ReturnType<typeof useT>

/** Текст отказа по коду. Незнакомый код — общая строка, а не пустота. */
function refusalText(t: T, problem: AuthProblem): string {
  if (problem.code === 'rate_limited') {
    return t('auth.problem.rate_limited', { count: problem.retryAfterSeconds ?? 60 })
  }
  return t(`auth.problem.${problem.code}`)
}

/** Замечание к полю: какое поле и что с ним не так. */
function fieldText(t: T, problem: ValidationProblem): string {
  return t(`auth.invalid.${problem.code}`, {
    field: t(`auth.field.${problem.field}`, { defaultValue: problem.field }),
    count: PASSWORD_MIN_LENGTH,
  })
}

/**
 * Текст любой неудачи запроса.
 *
 * Общая строка — только там, где причина действительно неизвестна.
 * Четыре случая, и они не сводятся друг к другу: сервер отказал
 * по своей причине (`AuthRefused`), значение не прошло проверку
 * (`ValidationRefused`), сервера не видно вовсе (сеть), сервер
 * ответил пятисоткой. Раньше все четыре показывались одним
 * «Не вышло. Попробуйте ещё раз» (D41).
 */
export function authFailureText(t: T, error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof AuthRefused) return refusalText(t, error.problem)
  if (error instanceof ValidationRefused) {
    return error.problems.map((problem) => fieldText(t, problem)).join(' ')
  }
  if (error instanceof ApiError) {
    // Сеть и пятисотка — разные советы: подождать связи или подождать
    // нас. «Попробуйте ещё раз» не годится ни там, ни там.
    if (error.status === null) return t('auth.unreachable')
    if (error.status >= 500) return t('auth.serverFailed')
  }
  return t('auth.failed')
}

function Refusal({ error }: { readonly error: unknown }) {
  const t = useT()
  const text = authFailureText(t, error)
  if (text === null) return null
  return (
    <p className="app-problems" role="alert">
      {text}
    </p>
  )
}

/** Поле почты и пароля: одно и то же в трёх формах из пяти. */
function Credentials({
  email,
  password,
  onEmail,
  onPassword,
  withHint,
}: {
  readonly email: string
  readonly password: string
  readonly onEmail: (value: string) => void
  readonly onPassword: (value: string) => void
  readonly withHint: boolean
}) {
  const t = useT()
  return (
    <>
      <label className="app-field">
        <span className="app-field-label">{t('auth.email')}</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => {
            onEmail(event.target.value)
          }}
        />
      </label>
      <label className="app-field">
        <span className="app-field-label">{t('auth.password')}</span>
        <input
          type="password"
          autoComplete={withHint ? 'new-password' : 'current-password'}
          required
          minLength={PASSWORD_MIN_LENGTH}
          value={password}
          onChange={(event) => {
            onPassword(event.target.value)
          }}
        />
        {withHint && (
          <span className="app-field-hint">
            {t('auth.passwordHint', { count: PASSWORD_MIN_LENGTH })}
          </span>
        )}
      </label>
    </>
  )
}

export function SignInScreen({
  api,
  session,
  onSignedIn,
  onSignUp,
  onForgot,
}: {
  readonly api: Api
  readonly session: Session
  readonly onSignedIn: (adoptable: readonly CharacterSummary[]) => void
  readonly onSignUp: () => void
  readonly onForgot: () => void
}) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void api
      .login({ email, password })
      .then((result) => {
        session.accept(result.tokens, result.profile)
        onSignedIn(result.adoptable)
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section className="app-screen app-auth">
      <h2>{t('auth.signIn')}</h2>
      <form className="app-form" onSubmit={submit}>
        <Credentials
          email={email}
          password={password}
          onEmail={setEmail}
          onPassword={setPassword}
          withHint={false}
        />
        <Refusal error={error} />
        <button type="submit" className="app-button" disabled={busy}>
          {busy ? t('auth.working') : t('auth.submitSignIn')}
        </button>
      </form>
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onSignUp}
        >
          {t('auth.toSignUp')}
        </button>
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onForgot}
        >
          {t('auth.forgot')}
        </button>
      </div>
    </section>
  )
}

export function SignUpScreen({
  api,
  locale,
  onSignIn,
}: {
  readonly api: Api
  /** Язык профиля (D11): на нём придут письма. */
  readonly locale: Locale
  readonly onSignIn: () => void
}) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void api
      .register({ email, password, locale })
      .then(() => {
        setSent(true)
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section className="app-screen app-auth">
      <h2>{t('auth.signUp')}</h2>
      {sent ? (
        <>
          <p className="app-auth-notice">{t('auth.registered')}</p>
          {/* Письмо уходит из очереди, а не в запросе (D41): между
              «аккаунт создан» и «письмо в ящике» есть зазор, и он
              бывает вечным — провайдер лежит, письмо в спаме. */}
          <ResendConfirmation api={api} email={email} />
        </>
      ) : (
        <form className="app-form" onSubmit={submit}>
          <Credentials
            email={email}
            password={password}
            onEmail={setEmail}
            onPassword={setPassword}
            withHint
          />
          <Refusal error={error} />
          <button type="submit" className="app-button" disabled={busy}>
            {busy ? t('auth.working') : t('auth.submitSignUp')}
          </button>
        </form>
      )}
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onSignIn}
        >
          {t('auth.toSignIn')}
        </button>
      </div>
    </section>
  )
}

/**
 * «Отправить письмо ещё раз» (D41).
 *
 * Единственное, что человек может сделать сам, когда письма нет.
 * Частота ограничена сервером; отказ показывается тем же способом,
 * что и остальные, — причиной, а не «не вышло».
 */
function ResendConfirmation({
  api,
  email,
}: {
  readonly api: Api
  readonly email: string
}) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [again, setAgain] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const resend = (): void => {
    setBusy(true)
    setError(null)
    void api
      .resendConfirmation(email)
      .then(() => {
        setAgain(true)
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <div className="app-actions">
      {again && <p className="app-auth-notice">{t('auth.resentDone')}</p>}
      <Refusal error={error} />
      <button
        type="button"
        className="app-button app-button-quiet"
        disabled={busy}
        onClick={resend}
      >
        {busy ? t('auth.working') : t('auth.resend')}
      </button>
    </div>
  )
}

export function ForgotPasswordScreen({
  api,
  onSignIn,
}: {
  readonly api: Api
  readonly onSignIn: () => void
}) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void api
      .requestPasswordReset(email)
      .then(() => {
        setSent(true)
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section className="app-screen app-auth">
      <h2>{t('auth.forgotTitle')}</h2>
      {/* Один и тот же ответ, зарегистрирован адрес или нет: разные
          ответы позволяют перебором собрать список аккаунтов. */}
      {sent ? (
        <p className="app-auth-notice">{t('auth.sent')}</p>
      ) : (
        <form className="app-form" onSubmit={submit}>
          <p>{t('auth.forgotText')}</p>
          <label className="app-field">
            <span className="app-field-label">{t('auth.email')}</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
              }}
            />
          </label>
          <Refusal error={error} />
          <button type="submit" className="app-button" disabled={busy}>
            {busy ? t('auth.working') : t('auth.forgotSubmit')}
          </button>
        </form>
      )}
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onSignIn}
        >
          {t('auth.toSignIn')}
        </button>
      </div>
    </section>
  )
}

/**
 * Подтверждение почты по ссылке из письма.
 *
 * Токен приходит фрагментом адреса, а не строкой запроса: фрагмент
 * не уходит на сервер вообще — ни в лог раздачи статики, ни
 * в `Referer` (см. `confirmEmailLink` в contracts).
 */
export function ConfirmEmailScreen({
  api,
  hash,
  onSignIn,
}: {
  readonly api: Api
  readonly hash: string
  readonly onSignIn: () => void
}) {
  const t = useT()
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working')
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    const token = tokenFromFragment(hash)
    if (token === null) {
      setState('failed')
      return
    }
    let cancelled = false
    void api
      .confirmEmail(token)
      .then(() => {
        if (!cancelled) setState('done')
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(cause)
        setState('failed')
      })
    return () => {
      cancelled = true
    }
  }, [api, hash])

  return (
    <section className="app-screen app-auth">
      <h2>{t('auth.confirmTitle')}</h2>
      {state === 'working' && <p>{t('auth.confirmWorking')}</p>}
      {state === 'done' && <p className="app-auth-notice">{t('auth.confirmDone')}</p>}
      {state === 'failed' && (
        <Refusal
          error={
            error ?? new AuthRefused({ code: 'invalid_token', retryAfterSeconds: null })
          }
        />
      )}
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onSignIn}
        >
          {t('auth.toSignIn')}
        </button>
      </div>
    </section>
  )
}

/** Установка нового пароля по ссылке. Гасит все сессии (D35). */
export function ResetPasswordScreen({
  api,
  hash,
  onSignIn,
}: {
  readonly api: Api
  readonly hash: string
  readonly onSignIn: () => void
}) {
  const t = useT()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const token = tokenFromFragment(hash)
    if (token === null) {
      setError(new AuthRefused({ code: 'invalid_token', retryAfterSeconds: null }))
      return
    }
    setBusy(true)
    setError(null)
    void api
      .resetPassword({ token, password })
      .then(() => {
        setDone(true)
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section className="app-screen app-auth">
      <h2>{t('auth.resetTitle')}</h2>
      {done ? (
        <p className="app-auth-notice">{t('auth.resetDone')}</p>
      ) : (
        <form className="app-form" onSubmit={submit}>
          <label className="app-field">
            <span className="app-field-label">{t('auth.password')}</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
              }}
            />
            <span className="app-field-hint">
              {t('auth.passwordHint', { count: PASSWORD_MIN_LENGTH })}
            </span>
          </label>
          <Refusal error={error} />
          <button type="submit" className="app-button" disabled={busy}>
            {busy ? t('auth.working') : t('auth.resetSubmit')}
          </button>
        </form>
      )}
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onSignIn}
        >
          {t('auth.toSignIn')}
        </button>
      </div>
    </section>
  )
}

/**
 * Предложение перенести драфты устройства на аккаунт (D33).
 *
 * Спрашиваем, а не переносим: за столом один ноутбук на партию,
 * и молчаливый перенос отдал бы мастеру драфты игроков. Отмечены
 * по умолчанию все — чаще всего это действительно свои, — но снять
 * отметку можно с любого, и «не переносить» ничего не удаляет.
 */
export function AdoptDraftsPrompt({
  api,
  drafts,
  onDone,
}: {
  readonly api: Api
  readonly drafts: readonly CharacterSummary[]
  readonly onDone: () => void
}) {
  const t = useT()
  const [chosen, setChosen] = useState<readonly string[]>(() =>
    drafts.map((draft) => draft.id),
  )
  const [busy, setBusy] = useState(false)

  const toggle = (id: string) => {
    setChosen((current) =>
      current.includes(id) ? current.filter((other) => other !== id) : [...current, id],
    )
  }

  const move = () => {
    if (chosen.length === 0) {
      onDone()
      return
    }
    setBusy(true)
    void api
      .adoptDrafts(chosen)
      .then(onDone)
      .catch(onDone)
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section className="app-screen app-auth">
      <h2>{t('auth.adopt.title')}</h2>
      <p>{t('auth.adopt.text')}</p>
      <ul className="app-list app-auth-drafts">
        {drafts.map((draft) => (
          <li key={draft.id}>
            <label className="app-check">
              <input
                type="checkbox"
                checked={chosen.includes(draft.id)}
                onChange={() => {
                  toggle(draft.id)
                }}
              />
              <span>{draft.name}</span>
              <code>{draft.code}</code>
            </label>
          </li>
        ))}
      </ul>
      <button type="button" className="app-button" disabled={busy} onClick={move}>
        {t('auth.adopt.submit')}
      </button>
      <button type="button" className="app-button app-button-quiet" onClick={onDone}>
        {t('auth.adopt.skip')}
      </button>
    </section>
  )
}
